import { useSearchParams } from 'react-router-dom';
import React, { useEffect, useState } from "react";
import { ROUTES } from "constants/routes-path";
import {
    Badge,
    Button,
    Center,
    Divider,
    Flex,
    Grid,
    Stack,
    Text,
    useColorModeValue
} from "@chakra-ui/react";
import CardHeader from "components/Card/CardHeader";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { queryService } from 'services/queryService';
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import Loading from "components/Loading/Loading";
import { FiSlash } from "react-icons/fi";

const BADGE_COLOR_BY_CODE = {
    411: "green",
    412: "blue",
    413: "orange",
    414: "green",
};

const QueriesViolationHistoryResult = () => {
    const [searchParams] = useSearchParams();
    const [response, setResponse] = useState(undefined);
    const showToast = useCustomToast();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const cardBg = useColorModeValue("gray.50", "gray.700");
    const textColor = useColorModeValue("gray.700", "white");
    const translationKey = "queries";
    const formTranslationKey = `${translationKey}.form`;

    const fetchData = async () => {
        if (response) setResponse(undefined);
        try {
            const data = await queryService.findViolationHistoryDetran(searchParams.get("valor"));
            setResponse(data);
        } catch (err) {
            showToast({ title: t(err), status: "error" });
            redirectToForm();
        }
    };

    const redirectToForm = () => {
        navigate(ROUTES.transportManagementPath + ROUTES.searches + ROUTES.searchViolationHistory);
    };

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <Flex direction="column" pt={{ sm: "125px", lg: "75px" }}>
            <Card px="0px" mb="24px">
                <CardHeader px="22px" mb="24px">
                    <Flex direction="column">
                        <Text color={textColor} fontSize="lg" fontWeight="bold">
                            {t(`${translationKey}.title.query-violation-history-detran`)}
                        </Text>
                    </Flex>
                </CardHeader>
                <CardBody px="22px">
                    {response ? (
                        <>
                            <Card border="1px solid #3884cc" bg={cardBg} mb="24px">
                                <CardHeader>
                                    <Text color={textColor} fontSize="md" fontWeight="bold">
                                        {t(`${formTranslationKey}.title.violation-history-data`)}
                                    </Text>
                                </CardHeader>
                                <Divider borderColor="#3884cc" mb="20px" />
                                <CardBody>
                                    <Grid templateColumns="repeat(4, 1fr)" gap="10px">
                                        <Text color={textColor} fontSize="md">
                                            <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.traffic-violation-ait.label`)}:
                                            </Text>{" "}
                                            {response.numeroAit ?? "-"}
                                        </Text>
                                        <Text color={textColor} fontSize="md">
                                            <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.vehicle-plate.label`)}:
                                            </Text>{" "}
                                            {response.placa ?? "-"}
                                        </Text>
                                        <Text color={textColor} fontSize="md">
                                            <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.control-number.label`)}:
                                            </Text>{" "}
                                            {response.numeroControleDetran ?? "-"}
                                        </Text>
                                        <Text color={textColor} fontSize="md">
                                            <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.violation-renainf.label`)}:
                                            </Text>{" "}
                                            {response.codigoRenainf ?? "-"}
                                        </Text>
                                    </Grid>
                                </CardBody>
                            </Card>

                            <Card border="1px solid #3884cc" bg={cardBg}>
                                <CardHeader>
                                    <Text color={textColor} fontSize="md" fontWeight="bold">
                                        {t(`${formTranslationKey}.title.violation-history-events`)}
                                    </Text>
                                </CardHeader>
                                <Divider borderColor="#3884cc" mb="20px" />
                                <CardBody>
                                    {!response.historico || response.historico.length === 0 ? (
                                        <Stack spacing={4}>
                                            <Text color={textColor}>
                                                <Center>
                                                    <FiSlash />{" "}
                                                    {t(`${formTranslationKey}.fields.traffic-violation-empty-message.label`)}
                                                </Center>
                                            </Text>
                                        </Stack>
                                    ) : (
                                        response.historico.map((evento, idx) => (
                                            <Stack key={idx} spacing={4} mb="16px">
                                                <Grid templateColumns="repeat(4, 1fr)" gap="10px" alignItems="center">
                                                    <Text color={textColor} fontSize="md">
                                                        <Badge colorScheme={BADGE_COLOR_BY_CODE[evento.codigoTransacao] ?? "gray"}>
                                                            {evento.codigoTransacao}
                                                        </Badge>
                                                    </Text>
                                                    <Text color={textColor} fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t(`${formTranslationKey}.fields.event-name.label`)}:
                                                        </Text>{" "}
                                                        {evento.nomeTransacao ?? "-"}
                                                    </Text>
                                                    <Text color={textColor} fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t(`${formTranslationKey}.fields.traffic-violation-date.label`)}:
                                                        </Text>{" "}
                                                        {evento.dataInclusao ?? "-"}
                                                    </Text>
                                                    <Text color={textColor} fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t(`${formTranslationKey}.fields.event-time.label`)}:
                                                        </Text>{" "}
                                                        {evento.horaInclusao ?? "-"}
                                                    </Text>
                                                </Grid>
                                                {idx < response.historico.length - 1 && <Divider mb="4px" />}
                                            </Stack>
                                        ))
                                    )}
                                </CardBody>
                            </Card>
                        </>
                    ) : (
                        <Loading maxH={{ sm: "100px", md: "350px" }} />
                    )}
                    <Flex justifyContent="flex-start">
                        <Button
                            variant="light"
                            fontSize="sm"
                            alignSelf="flex-end"
                            mt="24px"
                            w="125px"
                            onClick={() => redirectToForm()}
                        >
                            {t("system.actions.back")}
                        </Button>
                    </Flex>
                </CardBody>
            </Card>
        </Flex>
    );
};

export default QueriesViolationHistoryResult;
